<template>
  <div class="cc">
    <!-- Header : navigation + mode buttons -->
    <div class="cc-header">
      <div class="cc-nav">
        <template v-if="mode !== 'annee'">
          <button class="cc-nav-btn" :disabled="!canGoPrev" @click="goPrev">&#8249;</button>
          <!-- Label cliquable + icône calendrier -->
          <div class="cc-period-wrap">
            <span class="cc-period-label cc-period-label--clickable" @click="openDatePicker">{{ periodLabel }}</span>
            <button class="cc-calendar-btn" @click="openDatePicker" title="Choisir dans le calendrier">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </button>
            <!-- Input date caché, déclenché programmatiquement -->
            <input
              v-if="mode !== 'mois'"
              ref="datePicker"
              type="date"
              class="cc-hidden-input"
              :min="dataMin"
              :max="dataMax"
              :value="currentDateValue"
              @change="onDatePickerChange"
            />
            <input
              v-else
              ref="datePicker"
              type="month"
              class="cc-hidden-input"
              :min="dataMinMonth"
              :max="dataMaxMonth"
              :value="currentDateValue"
              @change="onDatePickerChange"
            />
          </div>
          <button class="cc-nav-btn" :disabled="!canGoNext" @click="goNext">&#8250;</button>
        </template>
        <span v-else class="cc-period-label">{{ periodLabel }}</span>
      </div>
      <div class="cc-modes">
        <button
          v-for="m in modes"
          :key="m.key"
          class="cc-mode-btn"
          :class="{ 'cc-mode-btn--active': mode === m.key }"
          @click="setMode(m.key)"
        >{{ m.label }}</button>
      </div>
    </div>

    <!-- Canvas Chart.js -->
    <div class="cc-canvas-wrap">
      <div v-if="loading" class="cc-loading">
        <div class="cc-spinner"></div>
      </div>
      <canvas ref="canvas"></canvas>
    </div>

    <div class="cc-unit">{{ unitLabel }}</div>
    <!-- légende seulement si la vue affichée contient des données extrapolées (12/10/2026) -->
    <div v-if="hasCalculated" class="cc-legend">
      <span><i class="cc-swatch cc-swatch--reel"></i>Données réelles</span>
      <span><i class="cc-swatch cc-swatch--calc"></i>Données extrapolées</span>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js'
import axios from 'axios'
import { authenticationService } from '@/services/authentication'
import { conf } from '@/config/config'

// Bilan de la Step 3 sur les dates réelles de la courbe (N01) : mois dans l'ordre chronologique avec leur année,
// calendriers bornés au fichier, titres et navigation sur les vraies dates ; mois incomplets et données calculées
// signalés (N03).
const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
const MONTH_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
const JS_DAY_NAMES = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.']
const COLOR = '#4a2080'
const BAR = 'rgba(74, 32, 128, 0.78)'
const CALC = '#f57c00'

const pad = n => String(n).padStart(2, '0')
/** 'aaaa-mm-jj' → Date locale (sans décalage de fuseau). */
const parseDate = iso => { const p = iso.split('-').map(Number); return new Date(p[0], p[1] - 1, p[2]) }
const round = (v, d) => (v === null || v === undefined ? null : parseFloat(v.toFixed(d)))

/** Motif hachuré orange pour les parts calculées des barres. */
function hatchPattern() {
  const c = document.createElement('canvas')
  c.width = 8; c.height = 8
  const ctx = c.getContext('2d')
  if (!ctx) return CALC
  ctx.fillStyle = 'rgba(245, 124, 0, 0.18)'
  ctx.fillRect(0, 0, 8, 8)
  ctx.strokeStyle = CALC
  ctx.lineWidth = 2
  ctx.beginPath(); ctx.moveTo(0, 8); ctx.lineTo(8, 0); ctx.stroke()
  ctx.beginPath(); ctx.moveTo(-2, 2); ctx.lineTo(2, -2); ctx.stroke()
  ctx.beginPath(); ctx.moveTo(6, 10); ctx.lineTo(10, 6); ctx.stroke()
  return ctx.createPattern(c, 'repeat') || CALC
}

export default {
  name: 'ConsumptionChart',
  props: {
    curveId: { type: Number, default: null },
    curveStats: { type: Object, default: null },
  },
  data() {
    return {
      mode: 'annee',
      monthIdx: 0,
      dayIdx: 0,
      weekIdx: 0,
      loading: false,
      chart: null,
      lastHasCalculated: false,
    }
  },
  computed: {
    modes() {
      return [
        { key: 'annee', label: 'Année' },
        { key: 'mois', label: 'Mois' },
        { key: 'semaine', label: 'Semaine' },
        { key: 'jour', label: 'Jour' },
      ]
    },
    dataYear() {
      return this.curveStats && this.curveStats.dataYear ? this.curveStats.dataYear : new Date().getFullYear()
    },
    /** Mois réels (chronologiques) ; pour d'anciennes réponses sans année, l'année du premier point. */
    months() {
      if (!this.curveStats || !this.curveStats.monthly) return []
      return this.curveStats.monthly.map((m, i) => ({
        idx: i,
        year: m.year || this.dataYear,
        month: m.month || i + 1,
        kwh: m.kwh || 0,
        calculatedKwh: m.calculatedKwh || 0,
      })).filter((m, i) => (this.curveStats.dailyByMonth[i] || []).length > 0)
    },
    /** Tous les jours de la courbe, dans l'ordre, avec leur date réelle. */
    allDates() {
      const out = []
      this.months.forEach(m => {
        (this.curveStats.dailyByMonth[m.idx] || []).forEach(d => {
          out.push({
            date: d.date || `${m.year}-${pad(m.month)}-${pad(d.day)}`,
            month: m.month, day: d.day, kwh: d.kwh || 0, calculatedKwh: d.calculatedKwh || 0,
          })
        })
      })
      return out
    },
    currentMonth() { return this.months[Math.min(this.monthIdx, this.months.length - 1)] || null },
    currentDate() { return this.allDates[Math.min(this.dayIdx, this.allDates.length - 1)] || null },
    weekStartDate() { return this.allDates[Math.min(this.weekIdx, this.allDates.length - 1)] || null },
    weekEndDate() { return this.allDates[Math.min(this.weekIdx + 6, this.allDates.length - 1)] || null },
    hasCalculated() {
      return this.lastHasCalculated
    },
    canGoPrev() {
      if (this.mode === 'mois') return this.monthIdx > 0
      if (this.mode === 'jour') return this.dayIdx > 0
      if (this.mode === 'semaine') return this.weekIdx > 0
      return false
    },
    canGoNext() {
      if (this.mode === 'mois') return this.monthIdx < this.months.length - 1
      if (this.mode === 'jour') return this.dayIdx < this.allDates.length - 1
      if (this.mode === 'semaine') return this.weekIdx + 7 < this.allDates.length
      return false
    },
    periodLabel() {
      if (this.mode === 'annee') {
        const f = this.months[0], l = this.months[this.months.length - 1]
        if (!f) return String(this.dataYear)
        if (f.year === l.year && f.month === 1 && l.month === 12) return String(f.year)
        return `${MONTH_SHORT[f.month - 1]} ${f.year} – ${MONTH_SHORT[l.month - 1]} ${l.year}`
      }
      if (this.mode === 'mois') return this.currentMonth ? `${MONTH_NAMES[this.currentMonth.month - 1]} ${this.currentMonth.year}` : '—'
      if (this.mode === 'jour') {
        if (!this.currentDate) return '—'
        const d = parseDate(this.currentDate.date)
        return `${JS_DAY_NAMES[d.getDay()]} ${d.getDate()} ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`
      }
      if (this.mode === 'semaine') {
        if (!this.weekStartDate) return '—'
        const a = parseDate(this.weekStartDate.date), b = parseDate(this.weekEndDate.date)
        const debut = a.getFullYear() === b.getFullYear()
          ? `${a.getDate()} ${MONTH_SHORT[a.getMonth()]}`
          : `${a.getDate()} ${MONTH_SHORT[a.getMonth()]} ${a.getFullYear()}`
        return `${debut} – ${b.getDate()} ${MONTH_SHORT[b.getMonth()]} ${b.getFullYear()}`
      }
      return ''
    },
    unitLabel() {
      return (this.mode === 'jour' || this.mode === 'semaine') ? 'Puissance (kW)' : 'Consommation (kWh)'
    },
    dataMin() { return this.curveStats && this.curveStats.startDate ? this.curveStats.startDate : (this.allDates[0] || {}).date || '' },
    dataMax() {
      if (this.curveStats && this.curveStats.endDate) return this.curveStats.endDate
      const l = this.allDates[this.allDates.length - 1]
      return l ? l.date : ''
    },
    dataMinMonth() { const m = this.months[0]; return m ? `${m.year}-${pad(m.month)}` : '' },
    dataMaxMonth() { const m = this.months[this.months.length - 1]; return m ? `${m.year}-${pad(m.month)}` : '' },
    currentDateValue() {
      if (this.mode === 'mois') return this.currentMonth ? `${this.currentMonth.year}-${pad(this.currentMonth.month)}` : ''
      if (this.mode === 'jour') return this.currentDate ? this.currentDate.date : ''
      if (this.mode === 'semaine') return this.weekStartDate ? this.weekStartDate.date : ''
      return ''
    },
  },
  watch: {
    curveStats(val) {
      if (val) this.initFromStats()
    },
  },
  mounted() {
    if (this.curveStats) this.initFromStats()
  },
  beforeDestroy() {
    if (this.chart) { this.chart.destroy(); this.chart = null }
  },
  methods: {
    openDatePicker() {
      const input = this.$refs.datePicker
      if (!input) return
      if (input.showPicker) {
        try { input.showPicker(); return } catch (e) { /* navigateur sans showPicker utilisable */ }
      }
      input.click()
    },
    onDatePickerChange(event) {
      const value = event.target.value
      if (!value) return
      if (this.mode === 'mois') {
        const p = value.split('-').map(Number)
        const idx = this.months.findIndex(m => m.year === p[0] && m.month === p[1])
        if (idx >= 0) { this.monthIdx = idx; this.refreshChart() }
        return
      }
      const idx = this._nearestDateIdx(value)
      if (this.mode === 'jour') this.dayIdx = idx
      else if (this.mode === 'semaine') this.weekIdx = this._clampWeek(idx)
      this.refreshChart()
    },
    _nearestDateIdx(iso) {
      const exact = this.allDates.findIndex(d => d.date === iso)
      if (exact >= 0) return exact
      const t = parseDate(iso).getTime()
      let best = 0, bestDiff = Infinity
      this.allDates.forEach((d, i) => {
        const diff = Math.abs(parseDate(d.date).getTime() - t)
        if (diff < bestDiff) { bestDiff = diff; best = i }
      })
      return best
    },
    /** Une semaine affichée reste entièrement dans la courbe : elle se termine au plus tard le dernier jour. */
    _clampWeek(idx) { return Math.max(0, Math.min(idx, this.allDates.length - 7)) },
    initFromStats() {
      this.monthIdx = 0
      this.dayIdx = 0
      this.weekIdx = 0
      this.refreshChart()
    },
    setMode(m) {
      this.mode = m
      this.refreshChart()
    },
    goPrev() {
      if (this.mode === 'mois' && this.monthIdx > 0) this.monthIdx--
      else if (this.mode === 'jour' && this.dayIdx > 0) this.dayIdx--
      else if (this.mode === 'semaine') this.weekIdx = this._clampWeek(this.weekIdx - 7)
      this.refreshChart()
    },
    goNext() {
      if (this.mode === 'mois' && this.monthIdx < this.months.length - 1) this.monthIdx++
      else if (this.mode === 'jour' && this.dayIdx < this.allDates.length - 1) this.dayIdx++
      else if (this.mode === 'semaine') this.weekIdx = this._clampWeek(this.weekIdx + 7)
      this.refreshChart()
    },
    async refreshChart() {
      if (this.mode === 'annee') this.renderAnnee()
      else if (this.mode === 'mois') this.renderMois()
      else if (this.mode === 'semaine') await this.fetchAndRenderSemaine()
      else if (this.mode === 'jour') await this.fetchAndRenderJour()
    },
    /** Barres : part mesurée + part extrapolée hachurée, empilées (un mois incomplet n'est pas distingué : 06/10/2026). */
    renderBars(labels, items) {
      const real = items.map(it => round(Math.max(0, it.kwh - it.calculatedKwh), 2))
      const calc = items.map(it => (it.calculatedKwh > 0.0001 ? round(it.calculatedKwh, 2) : null))
      // une part extrapolée invisible (moins de 0,5 % de la barre) ne fait pas afficher la légende
      this.lastHasCalculated = items.some(it => it.calculatedKwh > 0.0001 && it.calculatedKwh >= 0.005 * it.kwh)
      this.buildChart('bar', labels, [
        { label: 'Mesuré', data: real, backgroundColor: BAR, borderWidth: 0 },
        { label: 'Extrapolé', data: calc, backgroundColor: hatchPattern(), borderColor: CALC, borderWidth: 0 },
      ])
    },
    renderAnnee() {
      if (!this.curveStats) return
      const labels = this.months.map(m => `${MONTH_SHORT[m.month - 1]} ${String(m.year).slice(2)}`)
      this.renderBars(labels, this.months)
    },
    renderMois() {
      if (!this.curveStats || !this.currentMonth) return
      const days = this.allDates.filter(d => d.month === this.currentMonth.month && d.date.startsWith(String(this.currentMonth.year)))
      this.renderBars(days.map(d => String(d.day)), days)
    },
    /** Courbes : points mesurés en violet, points calculés en orange pointillé (séries séparées). */
    renderLines(labels, values, calculatedFlags) {
      const real = values.map((v, i) => (calculatedFlags[i] ? null : v))
      const calc = values.map((v, i) => (calculatedFlags[i] ? v : null))
      this.lastHasCalculated = calculatedFlags.some(Boolean)
      this.buildChart('line', labels, [
        { label: 'Mesuré', data: real, backgroundColor: 'rgba(74, 32, 128, 0.08)', borderColor: COLOR, borderWidth: 1.5, fill: true },
        { label: 'Extrapolé', data: calc, backgroundColor: 'rgba(245, 124, 0, 0.08)', borderColor: CALC, borderWidth: 1.5, borderDash: [5, 4], fill: true },
      ])
    },
    async fetchAndRenderSemaine() {
      if (!this.curveId || !this.weekStartDate) return
      this.loading = true
      try {
        const { month, day } = this.weekStartDate
        const token = authenticationService.status.jwtToken
        const resp = await axios.get(
          conf.API_URL + '/wizard/curve/' + this.curveId + '/week-data',
          { params: { month, day }, headers: { Authorization: 'Bearer ' + token } }
        )
        const pts = resp.data.data || []
        this.renderLines(pts.map(p => p.label), pts.map(p => round(p.kw, 3)), pts.map(p => !!p.calculated))
      } catch (e) { /* silencieux */ } finally {
        this.loading = false
      }
    },
    async fetchAndRenderJour() {
      if (!this.curveId || !this.currentDate) return
      this.loading = true
      try {
        const { month, day } = this.currentDate
        const token = authenticationService.status.jwtToken
        const resp = await axios.get(
          conf.API_URL + '/wizard/curve/' + this.curveId + '/day-data',
          { params: { month, day }, headers: { Authorization: 'Bearer ' + token } }
        )
        const pts = resp.data.data || []
        this.renderLines(pts.map(p => p.time), pts.map(p => round(p.watts / 1000, 3)), pts.map(p => !!p.calculated))
      } catch (e) { /* silencieux */ } finally {
        this.loading = false
      }
    },
    buildChart(type, labels, datasets) {
      if (this.chart) { this.chart.destroy(); this.chart = null }
      const canvas = this.$refs.canvas
      if (!canvas) return

      const isBar = type === 'bar'
      const isSemaine = this.mode === 'semaine'

      this.chart = new Chart(canvas, {
        type,
        data: {
          labels,
          datasets: datasets.map(d => Object.assign({ pointRadius: 0, lineTension: 0.2, spanGaps: false }, d)),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(item, data) {
                const v = item.yLabel
                if (v === null || v === undefined || isNaN(v)) return null
                const serie = data.datasets[item.datasetIndex].label
                return ' ' + serie + ' : ' + parseFloat(v).toFixed(2) + (isBar ? ' kWh' : ' kW')
              },
            },
            filter: function(item) { return item.yLabel !== null && item.yLabel !== undefined && !isNaN(item.yLabel) },
            backgroundColor: 'rgba(30, 20, 60, 0.88)',
            titleFontColor: '#fff',
            bodyFontColor: '#fff',
            borderColor: COLOR,
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: false,
          },
          scales: {
            xAxes: [{
              stacked: isBar,
              gridLines: { display: false },
              ticks: Object.assign(
                {
                  fontSize: 11,
                  fontColor: '#aaa',
                  maxRotation: 0,
                  autoSkip: !isSemaine,
                  maxTicksLimit: isSemaine ? undefined : (this.mode === 'jour' ? 9 : 13),
                },
                isSemaine ? { callback: function(value, index) { return index % 24 === 0 ? value : '' } } : {}
              ),
            }],
            yAxes: [{
              stacked: isBar,
              gridLines: { color: '#ececec', drawBorder: false, zeroLineColor: '#ddd' },
              ticks: {
                fontSize: 11,
                fontColor: '#aaa',
                beginAtZero: true,
                callback: function(v) {
                  if (v === 0) return '0'
                  if (isBar && v >= 1000) return (v / 1000).toFixed(1) + 'k'
                  return v
                },
              },
            }],
          },
        },
      })
    },
  },
}
</script>

<style scoped>
.cc {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #eee;
  padding: 16px 20px 12px;
}

.cc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
}

.cc-nav {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cc-nav-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.cc-nav-btn:hover:not(:disabled) {
  background: #f5f5f5;
  border-color: #aaa;
}

.cc-nav-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.cc-period-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
}

.cc-period-label {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  min-width: 160px;
  text-align: center;
}

.cc-period-label--clickable {
  cursor: pointer;
  text-decoration: underline dotted #aaa;
  text-underline-offset: 3px;
}

.cc-period-label--clickable:hover {
  color: #4a2080;
}

.cc-calendar-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  color: #888;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}

.cc-calendar-btn:hover {
  background: #f5f5f5;
  color: #4a2080;
  border-color: #aaa;
}

.cc-hidden-input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  pointer-events: none;
  top: 100%;
  left: 50%;
}

.cc-modes {
  display: flex;
  gap: 4px;
}

.cc-mode-btn {
  background: none;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 13px;
  cursor: pointer;
  color: #666;
  font-family: inherit;
  transition: background 0.15s, color 0.15s;
}

.cc-mode-btn:hover {
  background: #f5f5f5;
}

.cc-mode-btn--active {
  background: #4a2080;
  color: #fff;
  border-color: #4a2080;
  font-weight: 600;
}

.cc-canvas-wrap {
  position: relative;
  height: 280px;
}

.cc-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.7);
  z-index: 2;
}

.cc-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #eee;
  border-top-color: #4a2080;
  border-radius: 50%;
  animation: cc-spin 0.7s linear infinite;
}

@keyframes cc-spin { to { transform: rotate(360deg); } }

.cc-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px 18px;
  font-size: 11px;
  color: #777;
  margin-top: 4px;
}

.cc-swatch {
  display: inline-block;
  width: 12px;
  height: 10px;
  margin-right: 5px;
  vertical-align: -1px;
  border-radius: 2px;
}

.cc-swatch--calc {
  background: repeating-linear-gradient(135deg, #f57c00 0 2px, rgba(245, 124, 0, 0.18) 2px 5px);
}
.cc-swatch--reel {
  background: rgba(74, 32, 128, 0.78);
}


.cc-unit {
  font-size: 11px;
  color: #aaa;
  text-align: center;
  margin-top: 6px;
}
</style>
